import { z } from 'zod';
import React, { useContext } from 'react';

import routes from 'utils/routes';
import Form from 'components/form';
import SEO from 'components/SEO';
import AuthContext from 'context/AuthContext';
import useFormHandler from 'hooks/useFormHandler';
import { createEmailSchema } from 'utils/schemaBuilder';
import TermsAndPolicy from 'components/TermsAndPolicy';
import RightSideFeatures from 'components/RightSideFeatures';

const Signin = () => {
  const {
    actions: { login },
  } = useContext(AuthContext);

  const formSchema = z.object({
    email: createEmailSchema(true),
    password: z.string().min(1, 'Password is required'),
  });

  const { form, loading, error, setError } = useFormHandler(formSchema, {
    initialValues: {
      email: '',
      password: '',
    },
  });

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    setError(null);
    try {
      await login(values.email, values.password);
    } catch (err: any) {
      if (err?.response?.status === 401) {
        setError('Email or password is incorrect');
        return;
      }
      setError('An error occurred. Please try again.');
    }
  };

  return (
    <>
      <SEO 
        title="Sign in to your Nucleus AI Account"
        description="Access your free Nucleus AI receptionist dashboard. Manage your AI phone system, view call analytics, and train your virtual receptionist."
        keywords="AI receptionist login, virtual assistant dashboard, AI phone system, business automation"
      />
      <div className="flex-1 grid grid-cols-1 md:grid-cols-2 min-h-screen overflow-hidden">
        <div className="min-h-screen  overflow-y-auto md:overflow-y-hidden">
          <nav className="h-[70px] sticky top-0 w-full px-4 md:px-6 flex items-center justify-between">
            <a
              href="https://nucleus.com/"
              className="flex items-center gap-4"
              tabIndex={8}
            >
              <img src="/images/nucleus-logo.svg" alt="Nucleus Logo" className="h-8" />
            </a>
          </nav>
          <div className="md:flex-col items-center justify-center  lg:items-center signup-container extend px-4 md:px-0">
            <div className="mx-auto">
              <div className="text-center mb-3 max-w-[600px] mx-auto">
                <h1 className="text-xl md:text-2xl font-semibold text-[#5E6AB2] mb-1">
                  Sign in to your Nucleus AI Account
                </h1>
                <h2 className="text-base md:text-lg font-semibold text-gray-700 mb-4 text-center">
                  Access Your AI Dashboard
                </h2>
              </div>
              <div className="border custom-border-color bg-white rounded-xl p-4 md:p-6 md:w-5/6 xl:w-[440px] mx-auto">
                <div className="flex flex-col gap-2 mb-5">
                  <Form form={form} error={error} onSubmit={onSubmit}>
                    <Form.Input
                      name="email"
                      type="email"
                      label="Email"
                      placeholder="name@example.com"
                      tabIndex={1}
                    />
                    <Form.Password
                      name="password"
                      label="Password"
                      placeholder="Password"
                      tabIndex={2}
                    />
                    <Form.Submit loading={loading} disabled={loading} tabIndex={3}>
                      Sign in
                    </Form.Submit>
                  </Form>
                </div>
                <div className="flex items-center text-xs custom-text-color font-medium gap-2 mt-4 justify-center text-center">
                  <a href={routes.forgotPasswordPath.href} tabIndex={4} className="font-semibold text-[--primary-color]">Forgot password?</a>
                </div>
                <div className="flex items-center text-xs custom-text-color font-medium gap-2 mt-4 justify-center text-center">
                  Don't have an account?{' '}
                  <a href={routes.registerPath.href} tabIndex={5} className="font-semibold text-[--primary-color]">
                    Sign up
                  </a>
                </div>
              </div>
            </div>
          </div>
          <div className="w-full md:h-[50px]">
            <TermsAndPolicy termsTabIndex={6} privacyTabIndex={7} />
          </div>
        </div>
        <div className="min-h-screen hidden md:block bg-white border-l custom-border-color">
          <RightSideFeatures />
        </div>
      </div>
    </>
  );
};

export default Signin;
